import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { firstValueFrom, timeout } from 'rxjs';
import { albumOpenToStudent, deriveFlowMode, handoffGreetingOpen, isUsableAlbum } from '@lite-workspace/dto-fotoclass-book';
import { activeClientSession, isClientPreview } from './client-session';
// eslint-disable-next-line @nx/enforce-module-boundaries -- handoff mark from the eager ports entry, not the lazy editor
import { EditorOverviewState } from '@lite-workspace/album-editor/ports';
import { coverIncompleteOf, masterCoverIncomplete } from '../features/client/domain/master-cover-gate.util';
import { ClientSelectionStore } from '../features/client/domain/+state/client-selection.store';
import { ClientWizardApiService } from '../features/client/domain/infrastructure/client-wizard-api.service';
import { ProjectsApiService } from '../features/projects/domain/infrastructure/projects-api.service';
import { withTimeout } from '../features/projects/domain/utils/with-timeout.util';
import { ACCESS_READ_LIMIT_MS } from './route-wait';

/**
 * В режиме «собирает фотограф» редактор открывается ученику только со статуса «дизайн готов» и с
 * заполненной обложкой МАСТЕР-альбома проекта: прямая ссылка до готовности возвращает в портал, в
 * раздел «Альбом» — на экран ожидания макета, не в пустой редактор и не в альбом, который сервер
 * откажется утвердить.
 * Сервер закрыт независимо от этого guard'а — здесь только маршрут.
 */
export const studentEditorAccessGuard: CanActivateFn = async (route): Promise<boolean | UrlTree> => {
  // Без общей ссылки страницы выбора не существует — уводить некуда, маршрут остаётся за серверной авторизацией.
  const session = activeClientSession();
  if (!session?.linkId) return true;

  const projectId = route.paramMap.get('projectId');
  const albumId = route.paramMap.get('albumId');
  if (!projectId || !albumId) return true;

  const wizardApi = inject(ClientWizardApiService);
  const projectsApi = inject(ProjectsApiService);
  const router = inject(Router);
  const selection = inject(ClientSelectionStore);
  const overview = inject(EditorOverviewState);

  // Приветствие «Фотограф подготовил альбом» — у владельца альбома: пометку сервер снимает с его ПЕРВОГО
  // полного чтения (посадки или гарда), поэтому собственное чтение редактора отвечает false, и пометка
  // едет в редактор отсюда. Предпросмотр фотографа её не видит и не тратит.
  const latchGreeting = (ownReadMarked: boolean): void => {
    const current = activeClientSession();
    // Angular does not stop a superseded guard body: once another student took the tab, its writes are skipped.
    const sameStudent = current?.linkId === session.linkId && current?.personId === session.personId;
    const landingMarked = selection.albumId() === albumId && selection.albumUnseenChanges();
    if (!sameStudent || isClientPreview() || !(ownReadMarked || landingMarked)) return;
    overview.handoffDue.set(albumId);
    selection.ackAlbumUnseenChanges();
  };

  try {
    const config = await firstValueFrom(
      wizardApi.getWizardConfig(projectId, session.personId).pipe(timeout(ACCESS_READ_LIMIT_MS)),
    );
    const flow = deriveFlowMode({
      enabled: config?.selection.enabled ?? false,
      assemblyMode: config?.selection.assemblyMode === 'photographer' ? 'photographer' : 'student',
    });
    if (flow !== 'photographerAssembles') {
      // Альбом открыт ученику с создания — гейта нет, но своё полное чтение нужно: на холодном входе посадка
      // читает книгу параллельно, и пометка, снятая её чтением раньше редактора, иначе осталась бы в сторе вкладки.
      const own = await firstValueFrom(projectsApi.getPhotoBook(projectId, albumId).pipe(timeout(ACCESS_READ_LIMIT_MS))).catch(
        () => null,
      );
      if (handoffGreetingOpen(flow, own?.status ?? selection.albumStatus())) latchGreeting(own?.clientUnseenChanges === true);
      return true;
    }

    // Общие поля обложки диктует мастер-альбом проекта; его недоступность — единственный отказ,
    // который здесь закрывает дверь: второй инстанции у мастера нет, и сервер откажет всё равно.
    // Молчащий мастер — тот же отказ, что и сбой его чтения: дверь закрыта.
    const masterIncomplete = withTimeout(
      masterCoverIncomplete(projectsApi, projectId, config?.masterAlbumId),
      ACCESS_READ_LIMIT_MS,
    ).catch(() => true);
    // Книгу читаем целиком: лёгкая проекция модели не несёт, а без неё не видно пустых полей обложки.
    const album = await firstValueFrom(projectsApi.getPhotoBook(projectId, albumId).pipe(timeout(ACCESS_READ_LIMIT_MS))).catch(
      async (error: unknown) => {
        // Сбой или молчание своей книги дверь не открывает, если её уже закрыл мастер: без книги
        // исход тот же, что у закрытого мастера.
        if (await masterIncomplete) return null;
        throw error;
      },
    );
    const coverIncomplete = (await masterIncomplete) || coverIncompleteOf(album);
    // Готовность объявляется без предусловий — до появления модели редактор откроется пустым.
    const open = albumOpenToStudent(flow, album?.status, coverIncomplete) && isUsableAlbum(album);
    if (!open) return router.createUrlTree(['/client', session.linkId, 'album']);
    // Unless the landing greeting already spent it, the mark rides to the editor greeting here.
    if (handoffGreetingOpen(flow, album?.status)) latchGreeting(album?.clientUnseenChanges === true);

    return true;
  } catch {
    // Отказ ЧУЖОГО запроса (конфиг, своя книга при открытом мастере) вход не ломает: серверная авторизация
    // всё равно не отдаст черновик. Мастер сюда не попадает — его вердикт fail-closed выше.
    return true;
  }
};
